import React, { useState, useEffect } from 'react'
import Modal from "@mui/material/Modal";
import closeIcon from "../../../../../Assets/close-icon.svg"
import Button from '../../../../Button/Button';
import { serviceConsumer } from '../../../../../network/ServiceConsumer';
import Loader from "../../../../Loader/Loader";

// === NEW SPLIT PREVIEW MODAL (added March 2026) ===
// This modal replaces the manual dancer-selection flow for splits.
// Instead of manually checking dancers, it auto-groups them by DOB/age bucket
// and shows a preview of the proposed split.
//
// TO REVERT: Stop importing/rendering this component in MergeSplit.jsx.
// The old SplitModal.jsx is untouched and still works from MergeSplitFn.jsx via the edit icon.

const SplitPreviewModal = ({ modalState, competition, feisYear, onClose, onSuccess }) => {

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")
    const [dancers, setDancers] = useState([])
    const [ageGroups, setAgeGroups] = useState({})
    const [newCompCode, setNewCompCode] = useState("")
    const [newCompDescription, setNewCompDescription] = useState("")
    const [splitAgeKeys, setSplitAgeKeys] = useState([])

    // Fetch dancers on modal open
    useEffect(() => {
        if (modalState && competition?._id) {
            fetchDancers()
        }
    }, [modalState, competition?._id])

    const fetchDancers = async () => {
        try {
            setLoading(true)
            const url = `${process.env.REACT_APP_BASE_URL}/feis/competition/dancers/${competition._id}`
            const response = await serviceConsumer("GET", url)
            const fetchedDancers = response.result || []
            setDancers(fetchedDancers)
            groupDancersByAge(fetchedDancers)
            setLoading(false)
        } catch (err) {
            setError(err.messages || "Failed to fetch dancers")
            setLoading(false)
        }
    }

    // Group dancers by their DOB year -> age category
    // Formula: U_value = feisYear - birthYear (e.g. born 2016, feis 2026 -> U10)
    const groupDancersByAge = (dancerList) => {
        const groups = {}
        dancerList.forEach(dancer => {
            const birthday = dancer?.dancerData?.birthday
            const birthYear = typeof birthday === 'number' ? birthday : parseInt(birthday)
            if (!isNaN(birthYear)) {
                const ageCategory = `U${feisYear - birthYear}`
                if (!groups[ageCategory]) {
                    groups[ageCategory] = []
                }
                groups[ageCategory].push(dancer)
            }
        })
        setAgeGroups(groups)

        // Auto-select the youngest age group as the one to split off
        const sortedKeys = Object.keys(groups).sort((a, b) => {
            const numA = parseInt(a.replace('U', ''))
            const numB = parseInt(b.replace('U', ''))
            return numA - numB
        })

        if (sortedKeys.length >= 2) {
            const splitKey = sortedKeys[0]
            setSplitAgeKeys([splitKey])
            autoGenerateCode([splitKey])
        }
    }

    // Parse competition code and generate new code for the split-off age group(s)
    const autoGenerateCode = (selectedKeys) => {
        const code = competition?.code || ""
        const match = code.match(/^([A-Za-z]+?)(\d+)([A-Za-z]+)$/)

        if (selectedKeys.length === 1) {
            const ageNumber = selectedKeys[0].replace('U', '')
            if (match) {
                setNewCompCode(`${match[1]}${ageNumber}${match[3]}`)
            } else {
                setNewCompCode(`${code}-${selectedKeys[0]}`)
            }
        } else {
            // Multiple ages selected - use lowest age number
            const lowestAge = Math.min(...selectedKeys.map(k => parseInt(k.replace('U', ''))))
            if (match) {
                setNewCompCode(`${match[1]}${lowestAge}${match[3]}`)
            } else {
                setNewCompCode(`${code}-${selectedKeys.join('_')}`)
            }
        }
        setNewCompDescription(competition?.description || "")
    }

    // Toggle an age column selection (multi-select)
    // Cannot deselect all - at least one must be selected
    // Cannot select all - at least one must remain with original
    const handleToggleSplitAge = (ageKey) => {
        setSplitAgeKeys(prev => {
            let updated
            if (prev.includes(ageKey)) {
                // Deselecting - don't allow empty selection
                if (prev.length <= 1) return prev
                updated = prev.filter(k => k !== ageKey)
            } else {
                // Selecting - don't allow selecting ALL groups
                if (prev.length >= ageKeys.length - 1) return prev
                updated = [...prev, ageKey]
            }
            autoGenerateCode(updated)
            return updated
        })
    }

    // Confirm split - sends the split-off group's registration IDs + ages to the API
    const handleConfirmSplit = async () => {
        if (splitAgeKeys.length === 0) return

        // Collect all dancers from selected age groups
        const registrationIds = splitAgeKeys.flatMap(key =>
            (ageGroups[key] || []).map(dancer => dancer._id)
        )

        // Ages for the new (split-off) competition
        const newAges = [...splitAgeKeys]
        // Ages remaining on the original competition
        const originAges = ageKeys.filter(k => !splitAgeKeys.includes(k))

        const payload = {
            origin_competition: competition._id,
            competition_new_code: newCompCode,
            competition_new_description: newCompDescription,
            registrations: registrationIds,
            new_ages: newAges,
            origin_ages: originAges,
        }

        try {
            setLoading(true)
            const url = `${process.env.REACT_APP_BASE_URL}/feis/competition/split`
            await serviceConsumer("POST", url, payload)
            setSuccess("Competition split successfully!")
            setTimeout(() => {
                onSuccess && onSuccess()
                onClose()
            }, 1000)
            setLoading(false)
        } catch (err) {
            setError(err.messages || "Split failed")
            setLoading(false)
        }
    }

    const ageKeys = Object.keys(ageGroups).sort((a, b) => {
        const numA = parseInt(a.replace('U', ''))
        const numB = parseInt(b.replace('U', ''))
        return numA - numB
    })

    // Count total dancers being split off
    const splitDancerCount = splitAgeKeys.reduce((sum, key) =>
        sum + (ageGroups[key]?.length || 0), 0
    )

    return (
        <>
            {loading && <Loader />}
            <Modal sx={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "10px" }} open={modalState}>
                <div className="org-modal-div" style={{ maxWidth: "900px", width: "90vw", maxHeight: "80vh", overflow: "auto", padding: "24px" }}>
                    <div className="close-div mb-1">
                        <img src={closeIcon} alt="close-icon" className="close-icon" onClick={onClose} />
                    </div>
                    <p className="modal-org-title text-center my-2">Split Competition</p>
                    <p className="modal-org-subtitle text-center">
                        {competition?.code}-{competition?.description}
                    </p>

                    {ageKeys.length < 2 && !loading && (
                        <p className="text-center" style={{ color: "red", fontSize: "12px" }}>
                            This competition has only one age group and cannot be split.
                        </p>
                    )}

                    {ageKeys.length >= 2 && (
                        <>
                            {/* Preview columns */}
                            <div className="d-flex gap-3 mt-3" style={{ minHeight: "200px" }}>
                                {ageKeys.map(ageKey => {
                                    const isSplitOff = splitAgeKeys.includes(ageKey)
                                    const groupDancers = ageGroups[ageKey] || []
                                    const displayCode = isSplitOff ? newCompCode : competition?.code

                                    return (
                                        <div
                                            key={ageKey}
                                            onClick={() => handleToggleSplitAge(ageKey)}
                                            style={{
                                                flex: 1,
                                                minWidth: "180px",
                                                border: isSplitOff ? "2px solid #68AD3F" : "1px solid #ddd",
                                                borderRadius: "8px",
                                                padding: "12px",
                                                cursor: "pointer",
                                                backgroundColor: isSplitOff ? "#f0f9eb" : "#fff",
                                            }}
                                        >
                                            <div style={{ fontWeight: "bold", fontSize: "14px", marginBottom: "4px", whiteSpace: "nowrap" }}>
                                                {displayCode} — {ageKey}
                                                {isSplitOff && <span style={{ color: "#68AD3F", fontSize: "11px", marginLeft: "6px" }}>(NEW)</span>}
                                            </div>
                                            <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                                                {groupDancers.length} dancer{groupDancers.length !== 1 ? 's' : ''}
                                            </div>
                                            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                                {groupDancers.map((dancer, idx) => (
                                                    <div key={idx} style={{ fontSize: "12px", padding: "3px 0", borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                                                        #{dancer?.card_number} {dancer?.dancerData?.fname} {dancer?.dancerData?.lname}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            <p style={{ fontSize: "11px", color: "#999", textAlign: "center", marginTop: "8px" }}>
                                Click columns to select which age groups become the new competition. Selected: {splitAgeKeys.join(", ")} ({splitDancerCount} dancer{splitDancerCount !== 1 ? 's' : ''})
                            </p>

                            {/* New competition code & description fields */}
                            <div className="d-flex gap-3 mt-3">
                                <div className="d-flex flex-column" style={{ flex: 1 }}>
                                    <label className="label-head">New Competition Code:</label>
                                    <input
                                        className="split-input"
                                        type="text"
                                        value={newCompCode}
                                        onChange={(e) => setNewCompCode(e.target.value)}
                                        maxLength={30}
                                    />
                                </div>
                                <div className="d-flex flex-column" style={{ flex: 1 }}>
                                    <label className="label-head">Description:</label>
                                    <input
                                        className="split-input"
                                        type="text"
                                        value={newCompDescription}
                                        onChange={(e) => setNewCompDescription(e.target.value)}
                                    />
                                </div>
                            </div>

                            {success && <div style={{ color: "green", fontSize: "12px", marginTop: "8px", textAlign: "center" }}>{success}</div>}
                            {error && <div style={{ color: "red", fontSize: "12px", marginTop: "8px", textAlign: "center" }}>{error}</div>}

                            <div className="d-flex justify-content-center mt-3 mb-2">
                                <Button
                                    type="button"
                                    className={newCompCode && newCompDescription ? "filled" : "disabled"}
                                    disabled={!newCompCode || !newCompDescription}
                                    onClick={handleConfirmSplit}
                                >
                                    Confirm Split
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </>
    )
}

export default SplitPreviewModal
